export type LegacyYesNo = "Y" | "N";
export type HumanYesNo = "Yes" | "No";
export type ConsentValue = LegacyYesNo | HumanYesNo;
export type NullableConsent = ConsentValue | "" | null | undefined;

const LEGACY_TO_HUMAN: Record<LegacyYesNo, HumanYesNo> = {
  Y: "Yes",
  N: "No",
};

const HUMAN_TO_LEGACY: Record<HumanYesNo, LegacyYesNo> = {
  Yes: "Y",
  No: "N",
};

const isHumanYesNo = (value: string): value is HumanYesNo =>
  value === "Yes" || value === "No";

const isLegacyYesNo = (value: string): value is LegacyYesNo =>
  value === "Y" || value === "N";

const normalizeToken = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }

  if (isHumanYesNo(trimmed) || isLegacyYesNo(trimmed)) {
    return trimmed;
  }

  const upper = trimmed.toUpperCase();
  if (isLegacyYesNo(upper)) {
    return upper;
  }

  const lower = trimmed.toLowerCase();
  if (lower === "yes") {
    return "Yes";
  }
  if (lower === "no") {
    return "No";
  }

  return undefined;
};

export const toHumanYesNo = (value: unknown): HumanYesNo | undefined => {
  const normalized = normalizeToken(value);
  if (!normalized) {
    return undefined;
  }

  if (isHumanYesNo(normalized)) {
    return normalized;
  }

  if (isLegacyYesNo(normalized)) {
    return LEGACY_TO_HUMAN[normalized];
  }

  return undefined;
};

export const toLegacyYesNo = (value: unknown): LegacyYesNo | undefined => {
  const normalized = normalizeToken(value);
  if (!normalized) {
    return undefined;
  }

  if (isLegacyYesNo(normalized)) {
    return normalized;
  }

  if (isHumanYesNo(normalized)) {
    return HUMAN_TO_LEGACY[normalized];
  }

  return undefined;
};

export const isConsentAffirmative = (value: unknown): boolean =>
  toHumanYesNo(value) === "Yes";

export const coerceConsentToHuman = (
  value: unknown
): HumanYesNo | undefined => toHumanYesNo(value);

export const coerceConsentToLegacy = (
  value: unknown
): LegacyYesNo | undefined => toLegacyYesNo(value);

